import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo } from '../components/DemoContext';

// ---------------------------------------------------------------------------
// 7-Step Flow Data
// ---------------------------------------------------------------------------

interface FlowStep {
  step: number;
  title: string;
  modules: string[];
  modulePaths: Record<string, string>;
  description: string;
  detail: string;
  painReplaced: string;
  color: string;
  icon: string;
}

const STEPS: FlowStep[] = [
  {
    step: 1,
    title: 'Protocol Upload & Parse',
    modules: ['Protocol Import Engine', 'Patient Eligibility (M8)'],
    modulePaths: { 'Protocol Import Engine': '/protocol-setup', 'Patient Eligibility (M8)': '/safety' },
    description: 'Coordinator uploads protocol PDF. System parses visit schedule, procedure list, eligibility criteria, and timing windows automatically.',
    detail: 'The Protocol Import Engine uses pdfplumber to extract the Schedule of Assessments (SOA) grid, visit windows, and procedure lists from any clinical protocol PDF. Patient Eligibility (M8) applies spaCy NLP to parse inclusion/exclusion criteria into structured screening rules. Confidence scoring flags ambiguous extractions for manual review.',
    painReplaced: 'Eliminates manual coordinator form-building',
    color: '#4299e1',
    icon: '\uD83D\uDCC4',
  },
  {
    step: 2,
    title: 'Patient Enrollment',
    modules: ['Patients', 'IP Accountability'],
    modulePaths: { 'Patients': '/safety', 'IP Accountability': '/ip' },
    description: 'Patient screened against parsed eligibility rules and enrolled. Investigational product accountability begins \u2014 lot numbers, dispensing, and returns tracked from day one.',
    detail: 'Patients are screened against the M8 eligibility rules extracted from the protocol. Once enrolled, IP Accountability tracks every vial: lot number, dispensing timestamp, storage conditions, returns, and destruction. Each transaction is hash-chained for tamper-proof audit.',
    painReplaced: 'Eliminates paper-based IP logs',
    color: '#48bb78',
    icon: '\uD83D\uDC65',
  },
  {
    step: 3,
    title: 'Visit Execution',
    modules: ['Sample Log', 'Form Generator'],
    modulePaths: { 'Sample Log': '/samples', 'Form Generator': '/forms' },
    description: 'Nurse performs procedures. Blood draws and samples logged in Sample Log. Role-scoped forms auto-generated for Nurse and PI per visit. Data entered in Epic fires a FHIR R4 trigger to D3RI.',
    detail: 'The Form Generator creates visit-aware, role-scoped forms (Nurse: blood draw time, sample type, volume, signature; PI: finding, assessment, signature). Sample Log captures chain-of-custody for every specimen. When nurses enter data in Epic, a FHIR R4 Observation is fired to D3RI in real time.',
    painReplaced: 'Eliminates manual double entry from Epic to EDC',
    color: '#ecc94b',
    icon: '\uD83E\uDDEA',
  },
  {
    step: 4,
    title: 'Real-Time SDV & Validation',
    modules: ['Validation', 'Protocol Deviations', 'M6 SDV'],
    modulePaths: { 'Validation': '/validation', 'Protocol Deviations': '/deviations', 'M6 SDV': '/fhir-sim' },
    description: 'D3RI receives FHIR event and validates against protocol schedule in real time. Mismatches auto-flagged as Protocol Deviations with timestamp and severity. Validation confirms data completeness before EDC population.',
    detail: 'M6 SDV compares every incoming data point against the SOA grid: is this procedure expected at this visit? Is the value within clinical range? Is the timestamp within the visit window? Any deviation is logged with severity (low/medium/high) and linked to the evidence chain. The 8-layer FDA AI Validation Framework ensures model compliance.',
    painReplaced: 'Eliminates manual source data verification',
    color: '#ed8936',
    icon: '\u2705',
  },
  {
    step: 5,
    title: 'Evidence Capture & Locking',
    modules: ['Evidence Timeline', 'SAE Clock'],
    modulePaths: { 'Evidence Timeline': '/evidence', 'SAE Clock': '/sae' },
    description: 'Every data point timestamped and written to the Evidence Timeline as an immutable record. Serious Adverse Events trigger SAE Lock \u2014 freezing the complete evidence state at that moment for regulatory inspection.',
    detail: 'The bi-temporal evidence graph captures two timestamps for every event: when it happened (effectiveTimestamp) and when D3RI recorded it (internalKnowledgeTimestamp). SAE Clock enforces 21 CFR 312.32 reporting deadlines (7-day fatal/life-threatening, 15-day all others) with automated alerts at 50% and 80% of deadline.',
    painReplaced: 'Eliminates retroactive data reconstruction during audits',
    color: '#f56565',
    icon: '\uD83D\uDD12',
  },
  {
    step: 6,
    title: 'Decision Support',
    modules: ['Decision Twins', 'Digital Twins', 'Go/No-Go (M9)'],
    modulePaths: { 'Decision Twins': '/decisions', 'Digital Twins': '/patient-twin', 'Go/No-Go (M9)': '/decisions' },
    description: 'Digital Twin mirrors the patient\u2019s full clinical state across all data points. Decision Twin captures the exact evidence available at each committee decision. Go/No-Go recommendation generated with Monte Carlo confidence scoring.',
    detail: 'The Patient Digital Twin aggregates safety scores, PK/PD data, lab values, and adverse events into a single patient view. Decision Twin creates an immutable snapshot of the complete epistemic state at each SRC/DSMC vote \u2014 SHA-256 hash-chained. M9 Go/No-Go synthesizes all model outputs into a confidence-weighted recommendation.',
    painReplaced: 'Eliminates undocumented verbal decisions',
    color: '#9f7aea',
    icon: '\uD83E\uDDE0',
  },
  {
    step: 7,
    title: 'Audit & Inspection Ready',
    modules: ['Evidence Timeline', 'IP Accountability', 'Protocol Deviations'],
    modulePaths: { 'Evidence Timeline': '/evidence', 'IP Accountability': '/ip', 'Protocol Deviations': '/deviations' },
    description: 'Regulator or monitor traverses the complete bi-temporal evidence chain. Every decision is traceable to who knew what and when. IP dispensing, returns, and destruction fully accounted for.',
    detail: 'The cryptographic hash chain linking every state change provides tamper-evident audit. Temporal projection allows reconstructing exactly what was known at any historical moment T. Omission derivation identifies what evidence was missing at each decision point. The complete record is export-ready for FDA/EMA inspection.',
    painReplaced: 'Eliminates weeks of audit preparation',
    color: '#63b3ed',
    icon: '\uD83D\uDEE1\uFE0F',
  },
];

// Module glossary — all D3RI modules mapped to their steps
const MODULE_GLOSSARY: { name: string; steps: number[]; path: string; description: string }[] = [
  { name: 'Protocol Import Engine', steps: [1], path: '/protocol-setup', description: 'Parses protocol PDF into SOA grid, visit schedule, and eligibility criteria' },
  { name: 'Patient Eligibility (M8)', steps: [1], path: '/safety', description: 'NLP-based screening against parsed inclusion/exclusion criteria' },
  { name: 'Patients', steps: [2], path: '/safety', description: 'Patient enrollment, demographics, and safety monitoring' },
  { name: 'IP Accountability', steps: [2, 7], path: '/ip', description: 'Investigational product tracking: lot, dispensing, returns, destruction' },
  { name: 'Sample Log', steps: [3], path: '/samples', description: 'Chain-of-custody for blood draws and specimen tracking' },
  { name: 'Form Generator', steps: [3], path: '/forms', description: 'Auto-generates role-scoped Nurse and PI forms per protocol visit' },
  { name: 'Validation', steps: [4], path: '/validation', description: '8-layer FDA AI validation framework for model compliance' },
  { name: 'Protocol Deviations', steps: [4, 7], path: '/deviations', description: 'Auto-flagged protocol deviations with timestamp and severity' },
  { name: 'M6 SDV', steps: [4], path: '/fhir-sim', description: 'Source data verification: FHIR vs protocol schedule comparison' },
  { name: 'Evidence Timeline', steps: [5, 7], path: '/evidence', description: 'Immutable bi-temporal evidence chain with hash verification' },
  { name: 'SAE Clock', steps: [5], path: '/sae', description: '21 CFR 312.32 reporting deadlines with automated alerts' },
  { name: 'Decision Twins', steps: [6], path: '/decisions', description: 'Immutable epistemic snapshots at each committee decision' },
  { name: 'Digital Twins', steps: [6], path: '/patient-twin', description: 'Patient-level aggregation of all clinical data points' },
  { name: 'Go/No-Go (M9)', steps: [6], path: '/decisions', description: 'Monte Carlo confidence scoring for dose escalation recommendations' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function HowItWorks() {
  const navigate = useNavigate();
  const { startDemo } = useDemo();
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [showGlossary, setShowGlossary] = useState(false);

  const isLoggedIn = !!localStorage.getItem('d3ri_logged_in');

  const toggleStep = (step: number) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <span style={s.logo} onClick={() => navigate('/login')}>D3RI</span>
            <span style={s.logoSub}>Clinical Trial Intelligence</span>
          </div>
          <div style={s.headerRight}>
            <button style={s.glossaryBtn} onClick={() => setShowGlossary(!showGlossary)}>
              {showGlossary ? 'Hide' : 'Show'} Module Glossary
            </button>
            {isLoggedIn ? (
              <button style={s.backBtn} onClick={() => navigate(-1)}>Back to App</button>
            ) : (
              <button style={s.backBtn} onClick={() => navigate('/login')}>Sign In</button>
            )}
          </div>
        </div>

        {/* Title */}
        <div style={s.titleBlock}>
          <h1 style={s.title}>How D3RI Works</h1>
          <p style={s.subtitle}>
            End-to-end clinical trial decision infrastructure in 7 steps.
            <br />
            Click any step to explore the D3RI modules that power it.
          </p>
        </div>

        {/* Main content: steps + optional glossary */}
        <div style={s.mainRow}>
          {/* Steps */}
          <div style={s.stepsCol}>
            {STEPS.map((step, idx) => {
              const isExpanded = expandedStep === step.step;
              return (
                <React.Fragment key={step.step}>
                  {/* Connector arrow */}
                  {idx > 0 && (
                    <div style={s.connector}>
                      <div style={{ ...s.connectorLine, backgroundColor: STEPS[idx - 1].color }} />
                      <div style={{ ...s.connectorArrow, borderTopColor: STEPS[idx - 1].color }} />
                    </div>
                  )}

                  {/* Step card */}
                  <div
                    style={{
                      ...s.card,
                      borderLeftColor: step.color,
                      backgroundColor: isExpanded ? '#1e2a3a' : '#1a202c',
                    }}
                    onClick={() => toggleStep(step.step)}
                  >
                    {/* Top row */}
                    <div style={s.cardTop}>
                      <div style={{ ...s.stepBadge, backgroundColor: step.color }}>
                        <span style={s.stepIcon}>{step.icon}</span>
                        <span style={s.stepNum}>Step {step.step}</span>
                      </div>
                      <h3 style={s.cardTitle}>{step.title}</h3>
                      <span style={s.expandIcon}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                    </div>

                    {/* Module badges */}
                    <div style={s.moduleBadges}>
                      {step.modules.map((m) => (
                        <span
                          key={m}
                          style={{ ...s.moduleBadge, borderColor: step.color, color: step.color }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const path = step.modulePaths[m];
                            if (path && isLoggedIn) navigate(path);
                          }}
                        >
                          {m}
                        </span>
                      ))}
                    </div>

                    {/* Description */}
                    <p style={s.desc}>{step.description}</p>

                    {/* Pain replaced */}
                    <div style={s.painTag}>
                      <span style={s.painIcon}>{'\u2192'}</span>
                      <span style={s.painText}>{step.painReplaced}</span>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={s.detail}>
                        <div style={{ ...s.detailBar, backgroundColor: step.color }} />
                        <p style={s.detailText}>{step.detail}</p>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Glossary sidebar */}
          {showGlossary && (
            <div style={s.glossaryCol}>
              <div style={s.glossaryCard}>
                <h3 style={s.glossaryTitle}>D3RI Module Glossary</h3>
                <p style={s.glossarySub}>{MODULE_GLOSSARY.length} modules across 7 steps</p>
                {MODULE_GLOSSARY.map((mod) => (
                  <div
                    key={mod.name}
                    style={s.glossaryItem}
                    onClick={() => isLoggedIn && navigate(mod.path)}
                  >
                    <div style={s.glossaryName}>{mod.name}</div>
                    <div style={s.glossaryDesc}>{mod.description}</div>
                    <div style={s.glossarySteps}>
                      {mod.steps.map((sn) => {
                        const st = STEPS.find((x) => x.step === sn);
                        return (
                          <span key={sn} style={{ ...s.glossaryStepBadge, backgroundColor: st?.color || '#4a5568' }}>
                            Step {sn}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA */}
        <div style={s.ctaBlock}>
          <button
            style={s.ctaBtn}
            onClick={() => {
              startDemo();
              if (isLoggedIn) {
                navigate('/protocol-setup');
              } else {
                // Auto-login as coordinator for the demo
                localStorage.setItem('d3ri_logged_in', 'true');
                localStorage.setItem('d3ri_user_email', 'demo@d3ri.ai');
                localStorage.setItem('d3ri_user_name', 'Demo User');
                localStorage.setItem('d3ri_user_photo', '');
                localStorage.setItem('d3ri_selected_role', 'coordinator');
                navigate('/protocol-setup');
              }
            }}
          >
            Start Guided Demo {'\u2192'}
          </button>
          <p style={s.ctaSub}>
            {isLoggedIn
              ? 'Walk through all 7 steps with guided navigation'
              : 'Auto-signs in as Site Coordinator and walks you through all 7 steps'}
          </p>
        </div>

        {/* Footer */}
        <div style={s.footer}>
          <span style={s.footerText}>D3RI \u00b7 Decision, Data & Risk Intelligence \u00b7 PearX Demo 2026</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0d1117',
    color: '#e2e8f0',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    overflowX: 'hidden',
  },
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    padding: '24px 24px 48px',
  },

  /* Header */
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: { display: 'flex', alignItems: 'baseline', gap: 12 },
  headerRight: { display: 'flex', gap: 8 },
  logo: {
    fontSize: 28,
    fontWeight: 800,
    color: '#63b3ed',
    letterSpacing: 3,
    cursor: 'pointer',
  },
  logoSub: { fontSize: 12, color: '#718096' },
  glossaryBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid #2d3748',
    backgroundColor: '#1a202c',
    color: '#a0aec0',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  backBtn: {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid #4299e1',
    backgroundColor: 'transparent',
    color: '#63b3ed',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  /* Title */
  titleBlock: { textAlign: 'center', marginBottom: 40 },
  title: { fontSize: 32, fontWeight: 800, color: '#e2e8f0', margin: '0 0 8px' },
  subtitle: { fontSize: 15, color: '#a0aec0', lineHeight: 1.6, margin: 0 },

  /* Main layout */
  mainRow: { display: 'flex', gap: 24, alignItems: 'flex-start' },
  stepsCol: { flex: 1, minWidth: 0 },
  glossaryCol: { width: 300, flexShrink: 0 },

  /* Connector */
  connector: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 32,
    margin: '0 0 0 36px',
  },
  connectorLine: { width: 3, flex: 1, borderRadius: 2 },
  connectorArrow: {
    width: 0,
    height: 0,
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderTop: '8px solid #4a5568',
  },

  /* Step card */
  card: {
    backgroundColor: '#1a202c',
    border: '1px solid #2d3748',
    borderLeft: '4px solid',
    borderRadius: 8,
    padding: '16px 20px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  stepBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 16,
    flexShrink: 0,
  },
  stepIcon: { fontSize: 14 },
  stepNum: { fontSize: 11, fontWeight: 700, color: '#fff' },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#e2e8f0', margin: 0, flex: 1 },
  expandIcon: { fontSize: 10, color: '#718096', flexShrink: 0 },

  /* Module badges */
  moduleBadges: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 },
  moduleBadge: {
    padding: '2px 10px',
    borderRadius: 12,
    border: '1px solid',
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    backgroundColor: 'transparent',
  },

  /* Description */
  desc: { fontSize: 13, color: '#a0aec0', lineHeight: 1.6, margin: '0 0 10px' },

  /* Pain replaced */
  painTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 6,
    backgroundColor: '#22543d',
  },
  painIcon: { color: '#48bb78', fontSize: 12 },
  painText: { fontSize: 12, fontWeight: 600, color: '#48bb78' },

  /* Expanded detail */
  detail: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: '1px solid #2d3748',
    display: 'flex',
    gap: 12,
  },
  detailBar: { width: 3, borderRadius: 2, flexShrink: 0 },
  detailText: { fontSize: 13, color: '#cbd5e0', lineHeight: 1.7, margin: 0 },

  /* Glossary */
  glossaryCard: {
    backgroundColor: '#1a202c',
    border: '1px solid #2d3748',
    borderRadius: 8,
    padding: '16px 18px',
    position: 'sticky' as const,
    top: 24,
  },
  glossaryTitle: { fontSize: 15, fontWeight: 700, color: '#e2e8f0', margin: '0 0 4px' },
  glossarySub: { fontSize: 11, color: '#718096', margin: '0 0 16px' },
  glossaryItem: {
    padding: '10px 0',
    borderBottom: '1px solid #2d3748',
    cursor: 'pointer',
  },
  glossaryName: { fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 2 },
  glossaryDesc: { fontSize: 11, color: '#a0aec0', lineHeight: 1.4, marginBottom: 6 },
  glossarySteps: { display: 'flex', gap: 4 },
  glossaryStepBadge: {
    padding: '1px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 700,
    color: '#fff',
  },

  /* CTA */
  ctaBlock: { textAlign: 'center', marginTop: 48 },
  ctaBtn: {
    padding: '16px 48px',
    borderRadius: 10,
    border: 'none',
    backgroundColor: '#4299e1',
    color: '#fff',
    fontSize: 18,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background-color 0.15s',
  },
  ctaSub: { fontSize: 13, color: '#718096', marginTop: 12 },

  /* Footer */
  footer: { textAlign: 'center', marginTop: 48, paddingTop: 24, borderTop: '1px solid #2d3748' },
  footerText: { fontSize: 12, color: '#4a5568' },
};
